import { t } from '../../../../locale/texts';

import type { AutocompleteBounds, AutocompleteResponse, CarOption, FhhOption, FlightOption, HotelOption, PpOption } from '../../../types';

const assertString = (value: unknown, field: string): string => {
    if (typeof value !== 'string') {
        throw new TypeError(`Expected "${field}" to be a string, got ${typeof value}`);
    }
    return value;
};

const assertNumber = (value: unknown, field: string): number => {
    const number = Number(value);
    if (Number.isNaN(number)) {
        throw new TypeError(`"${field}" must be numeric, got ${JSON.stringify(value)}`);
    }
    return number;
};

const assertBoolean = (value: unknown, field: string): boolean => {
    if (typeof value !== 'boolean') {
        throw new TypeError(`Expected "${field}" to be a boolean, got ${typeof value}`);
    }
    return value;
};

const assertStringArray = (value: unknown): string[] => {
    if (!(Array.isArray(value) && value.every((item) => typeof item === 'string'))) {
        throw new Error('Expected an array of strings');
    }
    return value;
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const assertBounds = (bounds: unknown): AutocompleteBounds => {
    if (!isRecord(bounds)) {
        throw new TypeError(`"Bounds" must be an object, got ${JSON.stringify(bounds)}`);
    }

    const { southwest_lat, southwest_lng, northeast_lat, northeast_lng, latitude, longitude } = bounds;

    return {
        southwest_lat: assertNumber(southwest_lat, 'southwest_lat'),
        southwest_lng: assertNumber(southwest_lng, 'southwest_lng'),
        northeast_lat: assertNumber(northeast_lat, 'northeast_lat'),
        northeast_lng: assertNumber(northeast_lng, 'northeast_lng'),
        ...(latitude !== undefined && { latitude: assertNumber(latitude, 'latitude') }),
        ...(longitude !== undefined && { longitude: assertNumber(longitude, 'longitude') }),
    };
};

export const flightAutocompleteMapper = (res: AutocompleteResponse): FlightOption[] =>
    res.map((option) => {
        const id = assertString(option.value, 'value');
        const text = assertString(option.text, 'text');
        const city = assertString(option.cityName, 'city');
        const country = assertString(option.countryName, 'country');

        const valueSegment = (id.split('.')[1] ?? '').trim();
        const locationType: FlightOption['locationType'] =
            valueSegment === 'AIRPORT' ? 'AIRPORT' : valueSegment === 'METROPOLITAN_AREA' ? 'CITY' : 'UNKNOWN';

        return { id, text, locationType, city, country };
    });

export const fhhAutocompleteMapper = (res: AutocompleteResponse): FhhOption[] =>
    res
        .filter((option) => option.primary)
        .map((option) => ({
            id: assertString(option.primary, 'primary') + assertString(option.secondary ?? '', 'secondary'),
            text: assertString(option.destination, 'text'),
            count: assertNumber(option.numResults, 'count'),
            bounds: assertBounds(option.bounds),
            city: assertString(option.city ?? '', 'city'),
            country: assertString(option.country, 'country'),
        }));

export const carAutocompleteMapper = (res: AutocompleteResponse): CarOption[] =>
    res.map((option, index: number) => ({
        id: typeof option.code === 'string' ? option.code : index.toString(),
        text: assertString(option.name, 'name'),
        type: option.type === '1' ? t('car.autocomplete.airport') : t('car.autocomplete.area'),
        typeLogo: option.type === '8' ? 'assets/img/powered-by-google-white.png' : null,
        code: assertString(option.code, 'code'),
        placeId: assertString(option.placeId, 'placeId'),
        latitude: assertString(option.latitude, 'latitude'),
        longitude: assertString(option.longitude, 'longitude'),
    }));

export const hotelAutocompleteMapper = (res: AutocompleteResponse): HotelOption[] =>
    res.map((option) => {
        if (option.objectId) {
            return {
                id: assertString(option.objectId, 'objectId'),
                text: assertString(option.text, 'text'),
                type: assertString(option.type, 'type'),
                hierarchy: [],
                searchable: true,
                objectId: assertString(option.objectId, 'objectId'),
            };
        }
        return {
            id: `${option?.type === 'hotel' ? 'hotel' : 'place'}:${option?.key}`,
            text: assertString(option.text, 'text'),
            type: assertString(option.type, 'type'),
            hierarchy: assertStringArray(option.hierarchy),
            searchable: assertBoolean(option.searchable, 'searchable'),
            ...(Boolean(option.key) && { key: assertString(option.key, 'key') }),
            ...(Boolean(option.themePath) && { themePath: assertString(option.themePath, 'themePath') }),
        };
    });

export const ppAutocompleteMapper = (res: AutocompleteResponse): PpOption[] =>
    res
        .filter((option) => option.primary)
        .map((option) => ({
            id: assertString(option.primary, 'primary') + assertString(option.secondary ?? '', 'secondary'),
            text: assertString(option.destination, 'text'),
            count: assertNumber(option.numResults, 'count'),
            bounds: assertBounds(option.bounds),
            city: assertString(option.city ?? '', 'city'),
            country: assertString(option.country, 'country'),
            isCountry: assertBoolean(option.isCountry, 'isCountry'),
        }));
